// src/components/payroll/runs/PayRunList.js
import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import { 
  collection, query, where, orderBy, getDocs, startAfter, 
  limit, deleteDoc, doc, updateDoc
} from 'firebase/firestore';
import { useCurrency } from '../../../utils/currencyUtils';
import {
  Search, Filter, PlusCircle, Calendar, ChevronRight, 
  AlertCircle, MoreHorizontal, X, Eye, Edit, Trash2, Clock,
  Download, CheckCircle
} from 'lucide-react';

const PayRunList = () => {
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { formatAmount } = useCurrency();
  
  const [payRuns, setPayRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [actionMenu, setActionMenu] = useState(null);
  
  // Fetch pay runs
  useEffect(() => {
    const fetchPayRuns = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      if (!activeBusiness) {
        setError('Select a business before viewing payroll pay runs.');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Create base query
        let payRunsQuery = query(
          collection(db, 'payRuns'),
          where('businessId', '==', activeBusiness.id)
        );
        
        // Apply status filter if not 'all'
        if (statusFilter !== 'all') {
          payRunsQuery = query(
            payRunsQuery, // Continuing PayRunList.js
            where('status', '==', statusFilter)
          );
        }
        
        // Order by payment date descending
        payRunsQuery = query(
          payRunsQuery,
          orderBy('paymentDate', 'desc'),
          limit(10)
        );
        
        const snapshot = await getDocs(payRunsQuery);
        const payRunsList = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          payRunsList.push({
            id: doc.id,
            ...data,
            paymentDate: data.paymentDate?.toDate() || null,
            startDate: data.startDate?.toDate() || null,
            endDate: data.endDate?.toDate() || null,
            createdAt: data.createdAt?.toDate() || null
          });
        });
        
        setPayRuns(payRunsList);
        setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
        setHasMore(snapshot.docs.length === 10);
        
      } catch (err) {
        console.error('Error fetching pay runs:', err);
        setError('Failed to load pay runs. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPayRuns();
  }, [currentUser, activeBusiness, statusFilter]);
  
  // Load more pay runs
  const handleLoadMore = async () => {
    if (!hasMore || !lastVisible || loading) return;
    
    try {
      setLoading(true);
      
      let nextQuery = query(
        collection(db, 'payRuns'),
        where('businessId', '==', activeBusiness.id)
      );
      
      // Apply status filter if not 'all'
      if (statusFilter !== 'all') {
        nextQuery = query(
          nextQuery,
          where('status', '==', statusFilter)
        );
      }
      
      // Continue from last visible document
      nextQuery = query(
        nextQuery,
        orderBy('paymentDate', 'desc'),
        startAfter(lastVisible),
        limit(10)
      );
      
      const snapshot = await getDocs(nextQuery);
      const newPayRuns = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        newPayRuns.push({
          id: doc.id,
          ...data,
          paymentDate: data.paymentDate?.toDate() || null,
          startDate: data.startDate?.toDate() || null,
          endDate: data.endDate?.toDate() || null,
          createdAt: data.createdAt?.toDate() || null
        });
      });
      
      setPayRuns(prev => [...prev, ...newPayRuns]);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === 10);
      
    } catch (err) {
      console.error('Error loading more pay runs:', err);
      setError('Failed to load more pay runs. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete pay run
  const handleDeletePayRun = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pay run? This action cannot be undone.')) {
      return;
    }
    
    try {
      // Delete pay run document
      await deleteDoc(doc(db, 'payRuns', id));
      
      // Update local state
      setPayRuns(prev => prev.filter(payRun => payRun.id !== id));
      
      // Close action menu
      setActionMenu(null);
      
    } catch (err) {
      console.error('Error deleting pay run:', err);
      alert('Failed to delete pay run. Please try again.');
    }
  };
  
  // Filter pay runs by search term
  const getFilteredPayRuns = () => {
    if (!searchTerm.trim()) return payRuns;
    
    const searchLower = searchTerm.toLowerCase();
    return payRuns.filter(payRun => 
      payRun.name.toLowerCase().includes(searchLower) ||
      (payRun.notes && payRun.notes.toLowerCase().includes(searchLower))
    );
  };
  
  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Draft
          </span>
        );
      case 'processing':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex items-center">
            <Clock size={10} className="mr-1" />
            Processing
          </span>
        );
      case 'completed':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center">
            <CheckCircle size={10} className="mr-1" />
            Completed
          </span>
        );
      case 'failed':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Failed
          </span>
        );
      default:
        return null;
    }
  };
  
  const filteredPayRuns = getFilteredPayRuns();
  
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header with search and filters */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              className="pl-10 pr-4 py-2.5 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
              placeholder="Search pay runs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <select
                className="pl-10 pr-4 py-2.5 rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20 appearance-none"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            
            <Link
              to="/payroll/runs/new"
              className="px-4 py-2.5 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] flex items-center"
            >
              <PlusCircle size={18} className="mr-2" />
              New Pay Run
            </Link>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6">
        {loading && payRuns.length === 0 ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <AlertCircle size={24} className="mx-auto text-red-500 mb-2" />
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        ) : filteredPayRuns.length === 0 ? (
          <div className="text-center py-12">
            <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
            {payRuns.length === 0 ? (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No pay runs found</h3>
                <p className="text-gray-500 mb-6">
                  You haven't created any pay runs yet. Start by creating your first pay run.
                </p>
                <Link
                  to="/payroll/runs/new"
                  className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] inline-flex items-center"
                >
                  <PlusCircle size={18} className="mr-2" />
                  Create Pay Run
                </Link>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No matching pay runs</h3>
                <p className="text-gray-500 mb-4">
                  No pay runs match your current search or filter criteria.
                </p>
                <button
                  onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pay Run
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employees
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPayRuns.map((payRun) => (
                    <tr key={payRun.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="p-2 bg-purple-100 rounded-lg text-purple-700 mr-3">
                            <Calendar size={18} />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {payRun.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {payRun.startDate?.toLocaleDateString()} - {payRun.endDate?.toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {payRun.paymentDate?.toLocaleDateString() || 'Not set'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {getStatusBadge(payRun.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {payRun.employeeCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {formatAmount(payRun.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link
                            to={`/payroll/runs/${payRun.id}`}
                            className="text-[#190192] hover:text-[#2C0DB5]"
                          >
                            View
                          </Link>
                          <div className="relative">
                            <button
                              onClick={() => setActionMenu(actionMenu === payRun.id ? null : payRun.id)}
                              className="text-gray-400 hover:text-gray-600 p-1"
                            >
                              <MoreHorizontal size={16} />
                            </button>
                            
                            {actionMenu === payRun.id && (
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                                <div className="py-1">
                                  <Link
                                    to={`/payroll/runs/${payRun.id}`}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    <Eye size={16} className="mr-2 text-gray-500" />
                                    View Details
                                  </Link>
                                  {payRun.status === 'draft' && (
                                    <Link
                                      to={`/payroll/runs/${payRun.id}/edit`}
                                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                      <Edit size={16} className="mr-2 text-gray-500" />
                                      Edit
                                    </Link>
                                  )}
                                  {payRun.status === 'completed' && (
                                    <button
                                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                      onClick={() => {
                                        // PDF download functionality would be implemented here
                                        alert('Download functionality would be implemented here');
                                        setActionMenu(null);
                                      }}
                                    >
                                      <Download size={16} className="mr-2 text-gray-500" />
                                      Download Report
                                    </button>
                                  )}
                                  {payRun.status === 'draft' && (
                                    <button
                                      className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left"
                                      onClick={() => handleDeletePayRun(payRun.id)}
                                    >
                                      <Trash2 size={16} className="mr-2 text-red-500" />
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Load more button */}
            {hasMore && (
              <div className="mt-6 text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className={`px-4 py-2 border border-gray-300 rounded-lg ${
                    loading ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="inline-block animate-spin h-4 w-4 border-2 border-gray-500 rounded-full border-t-transparent mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PayRunList;
